import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ExternalLink, Trash, Check, Copy } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { AddContentModal } from '@/components/common/AddContentModal';
import { useNavigate } from 'react-router-dom';
import { useDeleteContentMutation, useGetContentsQuery } from '@/store/api/contentApi';
import { getLockedContentPath } from '@/utils/contentRoutes';
import ConfirmDeleteModal from '@/components/common/ConfirmDeleteModal';
import { DateRange } from '@/components/common/DateRangePicker';
import { truncate } from '@/utils/truncate';

export type ContentData = {
  id: string;
  title: string;
  curator: string;
  category: string;
  iconUrl: string;
  imageUrl: string;
  qrurl: string;
  duration: string;
  audiourl: string;
  playCount: string;
};

const CopyCell = ({ audiourl }: Pick<ContentData, 'audiourl'>) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!audiourl) return;

    try {
      await navigator.clipboard.writeText(audiourl);
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  }, [audiourl]);

  return (
    <div className="flex items-center gap-4 text-[#3C82F6]">
      {copied ? (
        <Check className="w-5 h-5 text-green-500 transition-all" />
      ) : (
        <Copy
          className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={handleCopy}
        />
      )}
    </div>
  );
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const getColumns = (
  navigate: (path: string) => void,
  onDelete: (id: string) => void
): ColumnDef<ContentData>[] => [
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <div
        className="flex items-center gap-2 cursor-pointer group"
        onClick={() => navigate(getLockedContentPath(row.original.id))}
      >
        <div className="flex items-center justify-center  group-hover:scale-105 transition-transform">
          <img src={row.original.imageUrl} alt="icon" className="w-8 h-8 rounded-lg shrink-0 " />
        </div>

        <div className="font-semibold text-[14px] text-[#1E0036] group-hover:text-[#9275C9] transition-colors break-words min-w-[120px]">
          {row.original.title}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'category',
    header: 'Category',
    cell: ({ row }) => (
      <span className="px-4 py-1.5 rounded-full text-[#1E0036] text-xs sm:text-sm font-semibold inline-block bg-[#FFF5D9]">
        {row.getValue('category')}
      </span>
    ),
  },
  {
    accessorKey: 'duration',
    header: 'Duration',
    cell: ({ row }) => (
      <span className="text-[#1E0036]">{formatDuration(Number(row.original.duration))}</span>
    ),
  },
  // {
  //   accessorKey: 'playCount',
  //   header: 'Play Count',
  //   cell: ({ row }) => <span className="text-[#1E0036] font-medium">{row.original.playCount}</span>,
  // },
  {
    id: 'copy',
    header: 'Copy',
    cell: ({ row }) => <CopyCell audiourl={row.original.qrurl} />,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <div className="flex items-center gap-4 text-[#3C82F6]">
        <ExternalLink
          className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => navigate(getLockedContentPath(row.original.id))}
        />
      </div>
    ),
  },
  {
    id: 'delete',
    header: 'Delete',
    cell: ({ row }) => (
      <div className="flex items-center gap-4 text-red-700">
        <Trash
          className="w-5 h-5 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => onDelete(row.original.id)}
        />
      </div>
    ),
  },
];
const formatDate = (date?: Date) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
const LockedData = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const queryParams = useMemo(
    () => ({
      isLocked: true,
      skip: (page - 1) * take,
      take,
      fromDate: formatDate(dateRange.from),
      toDate: formatDate(dateRange.to),
      search: search.trim() || undefined,
    }),
    [page, take, dateRange.from, dateRange.to, search]
  );

  const { data, refetch } = useGetContentsQuery(queryParams);

  const apiData = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const contents: ContentData[] = useMemo(
    () =>
      apiData.map((item) => ({
        id: String(item.id),
        title: item.title,
        curator: item.sourceType ?? '—',
        audiourl: item.audioUrl,
        qrurl: item.qrUrl,
        iconUrl: item.category?.iconUrl ?? '',
        imageUrl: item.imageUrl ?? '',
        category: item.category?.title ?? '',
        duration: `${item.duration ?? 0} `,
        playCount: String(item.playCount ?? 0),
      })),
    [apiData]
  );

  const [deleteContent] = useDeleteContentMutation();

  const handleTakeChange = useCallback((val: number) => {
    setTake(val);
    setPage(1);
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleDelete = useCallback((id: string) => {
    setDeleteId(id);
  }, []);

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteContent(Number(deleteId)).unwrap();
      setDeleteId(null);
      refetch();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const handleContentCreated = useCallback(() => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    refetch();
  }, [page, refetch]);

  const columns = useMemo(() => getColumns(navigate, handleDelete), [navigate, handleDelete]);

  return (
    <div className="p-3 sm:p-6 ">
      <DataTable
        data={contents}
        columns={columns}
        title="Content Library"
        addButtonText="Add New Content"
        onAddClick={openModal}
        totalCount={totalCount}
        isServerPaginated
        page={page}
        take={take}
        onPageChange={setPage}
        onTakeChange={handleTakeChange}
        enableAddNewBtn={true}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        enableSearch
        searchPlaceholder="Search by name"
        search={search}
        onSearchChange={handleSearchChange}
      />

      {isModalOpen && (
        <AddContentModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSuccess={handleContentCreated}
          isLocked={true}
        />
      )}
      {deleteId && (
        <ConfirmDeleteModal
          isOpen={!!deleteId}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default LockedData;
